import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { toast } from 'react-hot-toast'
import {
  BellIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  ShareIcon,
  ExclamationTriangleIcon,
  InboxIcon
} from '@heroicons/react/24/outline'
import { Button } from '@/components/common/Button'
import { AgentManagementService } from '@/services/api/services/AgentManagementService'
import type { InvitationResponse } from '@/services/api/models/InvitationResponse'
import { clsx } from 'clsx'

interface InvitationsPanelProps {
  refreshTrigger?: number
  onInvitationChange?: () => void
  onInvitationsUpdate?: (invitations: InvitationResponse[]) => void
  initialInvitations?: InvitationResponse[]
}

export function InvitationsPanel({ refreshTrigger, onInvitationChange, onInvitationsUpdate, initialInvitations }: InvitationsPanelProps) {
  const [allInvitations, setAllInvitations] = useState<InvitationResponse[]>(initialInvitations || [])
  const [loading, setLoading] = useState(!initialInvitations)
  const [actionLoading, setActionLoading] = useState<Record<string, 'accepting' | 'rejecting'>>({})
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'rejected'>('pending')
  const [error, setError] = useState<string | null>(null)

  const fetchInvitations = useCallback(async (isRefresh = false) => {
    try {
      // Only show loading state for initial load, not for refresh
      if (!isRefresh) {
        setLoading(true)
      }
      setError(null)
      
      // Fetch ALL invitations once, without status filter
      const response = await AgentManagementService.listMyInvitations({
        statusFilter: undefined
      })
      
      // Smart merge: only update invitations that have actually changed
      if (isRefresh) {
        setAllInvitations(prevInvitations => {
          // Create a map of new invitations for quick lookup
          const newInvitationsMap = new Map(response.map(inv => [inv.id, inv]))
          
          // Update existing invitations or add new ones
          const updatedInvitations = prevInvitations.map(prevInv => {
            const newInv = newInvitationsMap.get(prevInv.id)
            if (newInv) {
              // Only update if there are actual changes
              const hasChanges = JSON.stringify(prevInv) !== JSON.stringify(newInv)
              newInvitationsMap.delete(prevInv.id) // Remove from map as it's been processed
              return hasChanges ? newInv : prevInv
            }
            return prevInv
          })
          
          // Add any new invitations that weren't in the previous list
          const newInvitations = Array.from(newInvitationsMap.values())
          
          // Filter out invitations that are no longer in the response
          const invitationIds = new Set(response.map(i => i.id))
          const finalInvitations = updatedInvitations.filter(i => invitationIds.has(i.id))
          
          return [...finalInvitations, ...newInvitations]
        })
      } else {
        setAllInvitations(response)
      }
      
      // Update parent with the new invitation data
      onInvitationsUpdate?.(response)
    } catch (err: unknown) {
      console.error('Failed to fetch invitations:', err)
      if (!isRefresh) {
        setError(err instanceof Error ? err.message : 'Failed to load invitations')
      }
    } finally {
      if (!isRefresh) {
        setLoading(false)
      }
    }
  }, [onInvitationsUpdate])

  // Client-side filtering of invitations
  const filteredInvitations = useMemo(() => {
    if (filter === 'all') {
      return allInvitations
    }
    return allInvitations.filter(invitation => invitation.status === filter)
  }, [allInvitations, filter])

  // Track if we've done initial fetch
  const hasFetchedRef = useRef(false)
  const lastRefreshTriggerRef = useRef(refreshTrigger || 0)
  
  // Initial fetch when component mounts (only if no initial data provided)
  useEffect(() => {
    if (!hasFetchedRef.current && !initialInvitations) {
      hasFetchedRef.current = true
      fetchInvitations()
    } else if (initialInvitations) {
      // If initial invitations provided, mark as fetched and not loading
      hasFetchedRef.current = true
      setLoading(false)
    }
  }, [initialInvitations, fetchInvitations])
  
  // Handle refresh trigger changes
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > lastRefreshTriggerRef.current) {
      lastRefreshTriggerRef.current = refreshTrigger
      fetchInvitations(true) // Pass true for refresh
    }
  }, [fetchInvitations, refreshTrigger])

  // Update local state when initial invitations change (from parent refresh)
  const lastInitialInvitationsRef = useRef<InvitationResponse[] | undefined>(undefined)
  useEffect(() => {
    // Only update if the data has actually changed (not just a reference change)
    if (initialInvitations && 
        JSON.stringify(initialInvitations) !== JSON.stringify(lastInitialInvitationsRef.current)) {
      lastInitialInvitationsRef.current = initialInvitations
      
      // If no invitations, just set empty array
      if (initialInvitations.length === 0) {
        setAllInvitations([])
        return
      }
      
      // Smart merge to preserve any local state changes
      setAllInvitations(prevInvitations => {
        const newInvitationsMap = new Map(initialInvitations.map(inv => [inv.id, inv]))
        
        // Update existing invitations or add new ones
        const updatedInvitations = prevInvitations.map(prevInv => {
          const newInv = newInvitationsMap.get(prevInv.id)
          if (newInv) {
            newInvitationsMap.delete(prevInv.id)
            return newInv
          }
          return prevInv
        })
        
        // Add any new invitations that weren't in the previous list
        const newInvitations = Array.from(newInvitationsMap.values())
        
        // Filter out invitations that are no longer in the response
        const invitationIds = new Set(initialInvitations.map(i => i.id))
        const finalInvitations = updatedInvitations.filter(i => invitationIds.has(i.id))
        
        return [...finalInvitations, ...newInvitations]
      })
    }
  }, [initialInvitations])

  const handleAcceptInvitation = async (invitation: InvitationResponse) => {
    if (!invitation.id) return

    setActionLoading(prev => ({ ...prev, [invitation.id!]: 'accepting' }))

    try {
      await AgentManagementService.acceptInvitation({
        requestBody: {
          invitation_token: invitation.id
        }
      })

      toast.success(`Successfully joined "${invitation.agent_name}"`)
      
      // Update the invitation status in allInvitations
      const updatedInvitations = allInvitations.map(inv => 
        inv.id === invitation.id 
          ? { ...inv, status: 'accepted' as const }
          : inv
      )
      setAllInvitations(updatedInvitations)
      // Update parent with the new invitation data
      onInvitationsUpdate?.(updatedInvitations)
      
      onInvitationChange?.()
    } catch (err: unknown) {
      console.error('Failed to accept invitation:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to accept invitation')
    } finally {
      setActionLoading(prev => {
        if (!invitation.id) return prev
        const { [invitation.id]: _, ...rest } = prev
        return rest
      })
    }
  }

  const handleRejectInvitation = async (invitation: InvitationResponse) => {
    if (!invitation.id) return

    setActionLoading(prev => ({ ...prev, [invitation.id!]: 'rejecting' }))

    try {
      await AgentManagementService.rejectInvitation({
        invitationToken: invitation.id || '',
        requestBody: {}
      })

      toast.success('Invitation declined')
      
      // Update the invitation status in allInvitations
      const updatedInvitations = allInvitations.map(inv => 
        inv.id === invitation.id 
          ? { ...inv, status: 'rejected' as const }
          : inv
      )
      setAllInvitations(updatedInvitations)
      // Update parent with the new invitation data
      onInvitationsUpdate?.(updatedInvitations)
      
      onInvitationChange?.()
    } catch (err: unknown) {
      console.error('Failed to reject invitation:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to reject invitation')
    } finally {
      setActionLoading(prev => {
        if (!invitation.id) return prev
        const { [invitation.id]: _, ...rest } = prev
        return rest
      })
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckIcon className="w-4 h-4 text-green-500" />
      case 'rejected':
        return <XMarkIcon className="w-4 h-4 text-red-500" />
      case 'pending':
        return <ClockIcon className="w-4 h-4 text-yellow-500" />
      default:
        return <ClockIcon className="w-4 h-4 text-gray-400" />
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'accepted':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-full">
            Accepted
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded-full">
            Declined
          </span>
        )
      case 'pending':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 rounded-full">
            Pending
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300 rounded-full">
            Unknown
          </span>
        )
    }
  }

  const pendingCount = allInvitations.filter(inv => inv.status === 'pending').length

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <BellIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            {pendingCount > 0 && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {pendingCount > 9 ? '9+' : pendingCount}
                </span>
              </div>
            )}
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Agent Invitations
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Manage invitations to shared agents
            </p>
          </div>
        </div>
        
        <button
          type="button"
          onClick={() => { void fetchInvitations() }}
          disabled={loading}
          className={clsx(
            'p-2 rounded-lg transition-colors',
            loading
              ? 'text-gray-400'
              : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
          )}
          title="Refresh invitations"
        >
          <div className={clsx('w-5 h-5', loading && 'animate-spin')}>
            ⟳
          </div>
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {[
          { key: 'pending', label: 'Pending', count: allInvitations.filter(inv => inv.status === 'pending').length },
          { key: 'accepted', label: 'Accepted', count: allInvitations.filter(inv => inv.status === 'accepted').length },
          { key: 'rejected', label: 'Declined', count: allInvitations.filter(inv => inv.status === 'rejected').length },
          { key: 'all', label: 'All', count: allInvitations.length }
        ].map(({ key, label, count }) => (
          <button
            type="button"
            key={key}
            onClick={() => {
              const v = key
              if (v === 'all' || v === 'pending' || v === 'accepted' || v === 'rejected') {
                setFilter(v)
              }
            }}
            className={clsx(
              'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
              filter === key
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            )}
          >
            <span>{label}</span>
            {count > 0 && (
              <span className={clsx(
                'ml-2 px-2 py-1 text-xs rounded-full',
                filter === key
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
              )}>
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <ExclamationTriangleIcon className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { void fetchInvitations() }}
                className="mt-2"
              >
                Try Again
              </Button>
            </div>
          </div>
        ) : filteredInvitations.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <InboxIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {filter === 'pending' ? 'No pending invitations' : `No ${filter} invitations`}
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredInvitations.map((invitation) => (
              <div key={invitation.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <ShareIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {invitation.agent_name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          from {invitation.inviter_name}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(invitation.status || 'pending')}
                        {getStatusBadge(invitation.status || 'pending')}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-4">
                        <span className={clsx(
                          'px-2 py-1 rounded text-xs font-medium',
                          invitation.permission === 'write'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        )}>
                          {invitation.permission === 'write' ? 'Write Access' : 'Read Access'}
                        </span>
                        <span>
                          {invitation.created_at && formatDate(invitation.created_at)}
                        </span>
                      </div>
                    </div>

                    {invitation.expires_at && new Date(invitation.expires_at) > new Date() && (
                      <div className="mt-2 text-xs text-yellow-600 dark:text-yellow-400">
                        Expires {formatDate(invitation.expires_at)}
                      </div>
                    )}
                    
                    {invitation.expires_at && new Date(invitation.expires_at) <= new Date() && invitation.status === 'pending' && (
                      <div className="mt-2 text-xs text-red-600 dark:text-red-400">
                        Expired
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {invitation.status === 'pending' && 
                   invitation.expires_at && 
                   new Date(invitation.expires_at) > new Date() && (
                    <div className="flex items-center space-x-2 ml-4">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => { void handleRejectInvitation(invitation) }}
                        disabled={!!(invitation.id && actionLoading[invitation.id])}
                        className="text-red-600 hover:text-red-700 dark:text-red-400"
                      >
                        {invitation.id && actionLoading[invitation.id] === 'rejecting' ? (
                          <div className="w-4 h-4 animate-spin border-2 border-current border-t-transparent rounded-full" />
                        ) : (
                          <>
                            <XMarkIcon className="w-4 h-4" />
                            <span className="sr-only">Decline</span>
                          </>
                        )}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => { void handleAcceptInvitation(invitation) }}
                        disabled={!!(invitation.id && actionLoading[invitation.id])}
                      >
                        {invitation.id && actionLoading[invitation.id] === 'accepting' ? (
                          <div className="w-4 h-4 animate-spin border-2 border-current border-t-transparent rounded-full" />
                        ) : (
                          <>
                            <CheckIcon className="w-4 h-4" />
                            <span className="sr-only">Accept</span>
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}